import { listTablePrefs } from 'app/constants'
import DocumentMeta from 'core/components/DocumentMeta'
import ExternalLink from 'core/components/ExternalLink'
import SimpleLink from 'core/components/SimpleLink'
import ListContainer from 'core/containers/ListContainer'
import Badge from 'core/elements/badge'
import { GridViewColumn } from 'core/elements/grid/Grid'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { routeManager } from 'core/utils/routes'
import { IClusterDetailTabs } from 'k8s/components/dashboard/ClusterDetailTabs'
import { kaapiConfigMapsSelector } from 'k8s/components/kaapi/config-maps/selectors'
import Tooltip from 'core/elements/tooltip'
import { pick } from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { extractSupportedK8sVersions } from '../cluster/deployment/form-components/helpers'
import { getBadgeVariant, getClusterEndpoint, getClusterStatus } from '../dashboard/helpers'
import { listKaapiClusters } from '../kaapi/clusters/actions'
import { kaapiClustersOverviewSelector } from '../kaapi/clusters/selectors'
import { listKaapiConfigMaps } from '../kaapi/config-maps/actions'
import DeleteK8sClustersDialog from './DeleteK8sClustersDialog'
import K8sVersionCellComponent from './K8sVersionCellComponent'
import { DateAndTime } from 'core/components/listTable/cells/DateCell'
import ConfigureMetalLBModal from 'k8s/components/clusters-dashboard/ConfigureMetalLBModal'
import { listKaapiClusterAddons } from 'k8s/components/kaapi/cluster-addons/actions'
import { ClusterStatus } from '../kaapi/cluster-upgrade/model'

const usePrefParams = createUsePrefParamsHook('Clusters', listTablePrefs)

export const KaapiClusterStatusBadge = ({ cluster }) => {
  const clusterStatus = useMemo(() => getClusterStatus(cluster), [cluster])
  return (
    <Tooltip
      message={
        clusterStatus === 'Unhealthy' &&
        cluster?.status?.failureReason &&
        cluster?.status?.failureMessage
          ? `${cluster?.status?.failureReason}: ${cluster?.status?.failureMessage}`
          : null
      }
    >
      <Badge
        data-testid="overview-tab-emp-status"
        variant={getBadgeVariant(clusterStatus)}
        text={clusterStatus}
        ellipsisAt={20}
        tooltipBody={''}
      />
    </Tooltip>
  )
}

const defaultParams = {
  orderBy: 'metadata.creationTimestamp',
  orderDirection: 'desc',
}

const searchTargets = ['clusterName', 'clusterPhase']

const searchFunctions = [
  {
    property: 'clusterPhase',
    searchFn: (value, searchString, item) => {
      const status = getClusterStatus(item)
      return status.toLowerCase().includes(searchString.toLowerCase())
    },
  },
]

export default function ListK8sClusters() {
  const { params, getParamsUpdater } = usePrefParams(defaultParams)
  const { history } = useReactRouter()
  const { updatePrefs } = useScopedPreferences('k8sPluginGlobalParams')

  const [showConfigureMetalLBModal, setShowConfigureMetalLBModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedClusters, setSelectedClusters] = useState([])
  const [selectedDeleteRows, setSelectedDeleteRows] = useState([])

  // Load the list of clusters
  const { loading: loadingKaapiClusters, reload: reloadKaapiClusters } = useListAction(
    listKaapiClusters,
  )
  const { loading: loadingKaapiClusterAddons } = useListAction(listKaapiClusterAddons)
  const clusters = useSelector(kaapiClustersOverviewSelector)

  // Load the list of config maps
  const { loading: loadingConfigMaps } = useListAction(listKaapiConfigMaps)
  const configMaps = useSelector(kaapiConfigMapsSelector)
  // Get the supported K8s versions from the config maps
  const supportedK8sVersions = useMemo(() => extractSupportedK8sVersions(configMaps).sort(), [
    configMaps,
  ])

  // Reload the list of clusters on first load
  useEffect(() => {
    reloadKaapiClusters(true, false)
  }, [])

  const columns: GridViewColumn<any>[] = [
    {
      key: 'clusterName',
      label: 'Name',
      CellComponent: (props) => {
        return (
          <SimpleLink
            src=""
            onClick={() => {
              updatePrefs({
                cluster: props?.item?.metadata?.name,
                cluster_endpoint: getClusterEndpoint(props?.item?.spec?.controlPlaneEndpoint?.host),
                clusterId: props?.item?.metadata?.uid,
              })
              history.push(
                routeManager.routes.kubernetes.infrastructure.cluster.path({
                  cluster: props?.item?.metadata?.name,
                  tab: IClusterDetailTabs.OVERVIEW,
                }),
              )
            }}
          >
            {props?.item?.metadata?.name}
          </SimpleLink>
        )
      },
    },
    {
      key: 'clusterPhase',
      label: 'Status',
      CellComponent: (props) => <KaapiClusterStatusBadge cluster={props?.item} />,
    },
    {
      key: 'infrastructureProvider',
      label: 'Infrastructure Provider',
    },
    {
      key: 'hostedControlPlane.spec.version',
      label: 'K8s Version',
      CellComponent: ({ item }) => (
        <K8sVersionCellComponent item={item} supportedK8sVersions={supportedK8sVersions} />
      ),
    },

    // // TODO:: Add support period
    // {
    //   key: 'status.replicas',
    //   label: 'Support Period',
    //   formatFn: cellFormatFn,
    // },
    // // TODO:: need to confirm this and update the logic for the same currently added status logic
    // {
    //   key: 'status.readyReplicas',
    //   label: 'Nodes Status',
    //   //  TODO:: need to confirm this and update the logic for the same
    //   CellComponent: (props) => {
    //     const isClusterHealthy = isKaapiClusterHealthy(props?.item)

    //     return (
    //       <Badge
    //         data-testid={'overview-tab-emp-status'}
    //         variant={isClusterHealthy && !isClusterDeleting(props?.item) ? 'success' : 'error'}
    //         text={
    //           isClusterDeleting(props?.item)
    //             ? 'Deleting'
    //             : isClusterHealthy
    //             ? 'Healthy'
    //             : 'Unhealthy'
    //         }
    //         ellipsisAt={20}
    //         tooltipBody={''}
    //       />
    //     )
    //   },
    // },
    {
      key: 'hostedControlPlane.spec.hcpClass',
      label: 'Control Plane Class',
      width: 150,
      CellComponent: (props) => {
        return (
          <div style={{ width: '150px' }}>
            <Text variant="body2">{`Highly available - 3 masters(${props?.item?.hostedControlPlane?.spec?.hcpClass})`}</Text>
          </div>
        )
      },
    },
    {
      key: 'spec.controlPlaneEndpoint.host',
      label: 'API Endpoint',
      CellComponent: ({ item }) => (
        <Text variant="body2">{getClusterEndpoint(item?.spec?.controlPlaneEndpoint?.host)}</Text>
      ),
    },
    {
      key: 'metadata.creationTimestamp',
      label: 'Created',
      CellComponent: DateAndTime,
    },
  ]
  const batchActions = useMemo(
    () => [
      {
        label: 'Delete',
        icon: 'trash-alt',
        cond: (selectedItems) =>
          selectedItems?.every((item) => item?.clusterPhase !== ClusterStatus.Deleting),
        disabledTooltip: (selectedItems) =>
          selectedItems?.some((item) => item?.clusterPhase === ClusterStatus.Deleting)
            ? 'Selected clusters are already being deleted'
            : '',
        handleAction: (selectedItems) => {
          setSelectedDeleteRows(selectedItems)
          setShowDeleteModal(true)
        },
      },
      {
        label: 'Expose LB',
        icon: 'gear',
        refreshAfterSuccess: true,
        handleAction: (cluster) => {
          setSelectedClusters(cluster)
          setShowConfigureMetalLBModal(true)
        },
        cond: (clusters) => {
          if (clusters?.length !== 1) return false
          return clusters[0]?.isMetalLBAddon
        },
        disabledTooltip: (clusters) => {
          if (clusters?.length !== 1) return 'Select a single cluster to configure MetalLB'
          if (!clusters[0]?.isMetalLBAddon)
            return 'Selected cluster does not have MetalLB addon configured'
          return ''
        },
      },
    ],
    [setShowConfigureMetalLBModal, setSelectedClusters],
  )

  return (
    <>
      <DocumentMeta title="Clusters" />
      {showConfigureMetalLBModal && (
        <ConfigureMetalLBModal
          onClose={() => setShowConfigureMetalLBModal(false)}
          rows={selectedClusters}
        />
      )}
      {showDeleteModal && (
        <DeleteK8sClustersDialog
          rows={selectedDeleteRows}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
      <ListContainer<any, any>
        searchTargets={searchTargets}
        searchFunctions={searchFunctions}
        uniqueIdentifier="uid"
        loading={loadingKaapiClusters || loadingConfigMaps || loadingKaapiClusterAddons}
        loadingMessage="Loading Clusters..."
        onRefresh={reloadKaapiClusters}
        multiSelection
        data={clusters}
        columns={columns}
        getParamsUpdater={getParamsUpdater}
        label="Clusters"
        showItemsCountInLabel
        showBreadcrumbs={false}
        batchActions={batchActions}
        emptyContent="No clusters found"
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
